import { createSignal, For, JSXElement, mergeProps, Show } from 'solid-js';
import { useQueryClient } from '@tanstack/solid-query';
import { getKbLinkTransformer, Localized } from '~/i18n';
import { appConfig, externalLinks } from '~/consts.ts';
import { ApiConfigData, generateNewClientSecret } from '~/api';
import { getLocalizedString } from '~/i18n/utils.ts';
import logger from '~/utils/logger';
import ClientSecretItem from '~/pages/Settings/APICredentials/ClientSecretItem.tsx';
import GenerateSecretModal from '~/pages/Settings/APICredentials/GenerateSecretModal.tsx';
import {
  ClientSecretFlowEnum,
  getApiCredentialsTranslationKey,
  getQueryKeysForRefetching
} from '~/pages/Settings/utils.ts';
import ClientSecretsEmptyState from '~/pages/Settings/APICredentials/ClientSecretsEmptyState.tsx';
import { Button } from '~/components/ui/Button';
import { twMerge } from 'tailwind-merge';

type ClientSecretProps = {
  clientCredentials: ApiConfigData;
  flowType?: ClientSecretFlowEnum;
  class?: string;
};

export default function ClientSecrets(props: ClientSecretProps): JSXElement {
  const merged = mergeProps({}, props);
  const queryClient = useQueryClient();

  const [generatedSecret, setGeneratedSecret] = createSignal<string>('');
  const [isCtaLoading, setIsCtaLoading] = createSignal(false);

  return (
    <div class={twMerge(`p-6 pt-0`, merged.class)}>
      <div class={`flex justify-between items-start`}>
        <div class={`flex-grow`}>
          <span class={'text-sm text-fg-default font-medium'}>
            <Localized
              translationKey={getApiCredentialsTranslationKey(
                'section_header_client_secrets'
              )}
            />
          </span>
          <p class={`mt-1 text-sm text-fg-subtle`}>
            {
              <Localized
                translationKey={getApiCredentialsTranslationKey(
                  'section_subheader_client_secrets'
                )}
                vars={{
                  kbLink: getKbLinkTransformer(
                    externalLinks.kbArticles.clientSecret,
                    'settings-client-secret-guide'
                  )
                }}
              />
            }
          </p>
        </div>
        <div class={`flex self-stretch items-center`}>
          <Button
            disabled={
              merged.clientCredentials.secrets?.length >=
              appConfig.maxSupportedClientSecrets
            }
            variant={`outline`}
            size={`sm`}
            onClick={async () => {
              try {
                setIsCtaLoading(true);
                const { plainSecret } = await generateNewClientSecret(
                  merged.clientCredentials.id
                );
                setIsCtaLoading(false);
                setGeneratedSecret(plainSecret);
              } catch (e) {
                setIsCtaLoading(false);
                logger.error(e);
              }
            }}
            loading={isCtaLoading()}
            data-testid={'settings-client-secret-generate'}
          >
            {getLocalizedString('cta_labels.generate_client_secret')}
          </Button>
        </div>
      </div>
      <GenerateSecretModal
        secretText={generatedSecret()}
        onClose={async () => {
          setGeneratedSecret('');
          await queryClient.refetchQueries({
            queryKey: getQueryKeysForRefetching({
              flowType: merged.flowType,
              applicationIdForMultiApp: merged.clientCredentials.id
            }),
            type: 'active',
            exact: true
          });
        }}
      />
      <Show
        when={merged.clientCredentials.secrets.length}
        fallback={<ClientSecretsEmptyState />}
      >
        <div class={`w-full flex flex-col gap-6 mt-4`}>
          <For each={merged.clientCredentials.secrets}>
            {(secret) => (
              <ClientSecretItem
                clientId={merged.clientCredentials.id}
                secret={secret}
                allowRevoke={merged.clientCredentials.secrets?.length > 1}
                flowType={props?.flowType}
              />
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}
